import React from 'react';
import { QuestionField } from './QuestionField';
import type { ScreenQuestion } from '../../features/flow/types';
import {
  cleanQuestionText,
  escapeRegExp,
  extractTemplateIndicesFromIds,
  isNonInputQuestionFieldType,
  isQuestionLayoutWideLabel,
  isRequiredQuestionAnswerSatisfied,
  NON_RESIDENT_TAX_PAYMENT_IBAN_SCREEN_ID,
  questionGridCellClass,
} from '../../features/flow/utils';
import type { SessionScreen } from '../../features/flow/session';

function requiredFieldErrorMessage(
  q: ScreenQuestion,
  answers: Record<string, unknown>,
  cleanedScreenTitle: string
): string | null {
  if (!q.id || isNonInputQuestionFieldType(q.field_type)) return null;
  if (!q.required) return null;
  if (!cleanQuestionText(q.question_text ?? '', cleanedScreenTitle).trim()) return null;
  if (!isRequiredQuestionAnswerSatisfied(q, answers[q.id])) {
    return 'This field is required.';
  }
  return null;
}

export function QuestionFormScreenBody({
  questions,
  sessionScreen,
  isReviewScreen,
  showValidationErrors,
  answers,
  answersForShowIf,
  cleanedScreenTitle,
  actionBusy,
  setAnswers,
  evaluateShowIfFn,
  onAddItem,
  onDeleteItem,
  fieldNote,
}: {
  questions: ScreenQuestion[];
  sessionScreen: SessionScreen;
  isReviewScreen: boolean;
  showValidationErrors: boolean;
  answers: Record<string, unknown>;
  answersForShowIf: Record<string, any>;
  cleanedScreenTitle: string;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  evaluateShowIfFn: (condition: unknown, answers: Record<string, any>) => boolean;
  onAddItem: (template?: string) => void;
  onDeleteItem: (template?: string, idx?: number) => void;
  fieldNote?: (fieldId: string) => React.ReactNode;
}) {
  const blocks: React.ReactNode[] = [];
  let gridCells: React.ReactNode[] = [];
  let gridBlockIndex = 0;
  let coownershipSkipBlock: React.ReactNode | undefined;
  const isAeatPaymentDetailsScreen = sessionScreen.screen_id === NON_RESIDENT_TAX_PAYMENT_IBAN_SCREEN_ID;
  const quoteInputsLocked = Boolean(sessionScreen.quote_inputs_locked);

  const flushGrid = () => {
    if (gridCells.length === 0) return;

    blocks.push(
      <div
        key={`question_grid_${gridBlockIndex}`}
        className="w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start"
      >
        {gridCells}
      </div>
    );

    gridBlockIndex += 1;
    gridCells = [];
  };

  const addItemsByTemplate = new Map<string, ScreenQuestion>();
  for (const q of questions) {
    if (q.field_type === 'add_item' && q.item_template) {
      addItemsByTemplate.set(q.item_template, q);
    }
  }

  for (const q of questions) {

    const shouldShow = evaluateShowIfFn(q.show_if, answersForShowIf);
    if (!shouldShow) continue;

    if (q.field_type === 'add_item') continue;

    // AEAT Tax Payment Details screen should show only the actual text fields
    // (IBAN and account holder). Hide tax display rows that reuse the
    // tax_* field_type components.
    if (isAeatPaymentDetailsScreen && String(q.field_type ?? '').toLowerCase().trim() !== 'text') continue;

    if (
      !isNonInputQuestionFieldType(q.field_type) &&
      !cleanQuestionText(q.question_text ?? '', cleanedScreenTitle).trim()
    ) {
      continue;
    }

    if (
      sessionScreen.screen_id === 'co_ownership_details' &&
      q.id === 'coownership_skip' &&
      q.field_type === 'checkbox'
    ) {
      {
        const coSkipErr = requiredFieldErrorMessage(q, answers, cleanedScreenTitle);
        coownershipSkipBlock = (
          <div key={q.id} className="w-full max-w-[668px]">
            <div className="flex flex-col gap-3">
              <QuestionField
                q={q}
                answers={answers}
                setAnswers={setAnswers}
                cleanedScreenTitle={cleanedScreenTitle}
                sessionScreenId={sessionScreen.screen_id}
                actionBusy={actionBusy}
                inputsLocked={quoteInputsLocked}
              />
              {showValidationErrors && coSkipErr ? (
                <p className="text-sm text-red-600" role="alert">
                  {coSkipErr}
                </p>
              ) : null}
            </div>
          </div>
        );
      }
      continue;
    }

    if (q.field_type === 'section_header') {
      const headerId = String(q.id ?? '');
      let matchedTemplate: string | null = null;
      let sectionIdx: number | null = null;

      for (const [template] of addItemsByTemplate.entries()) {
        const m = headerId.match(new RegExp(`^${escapeRegExp(template)}_(\\d+)_`));
        if (m) {
          matchedTemplate = template;
          sectionIdx = Number(m[1]);
          break;
        }
      }

      flushGrid();

      if (!matchedTemplate || sectionIdx == null || Number.isNaN(sectionIdx)) {
        blocks.push(
          <div key={q.id} className="w-full min-w-0">
            <div className="text-sm font-semibold text-[#131315]">{q.question_text}</div>
          </div>
        );
        continue;
      }

      const shownIndices = extractTemplateIndicesFromIds(
        questions.map((qq) => qq.id),
        matchedTemplate
      );
      const maxIdx = shownIndices.length > 0 ? Math.max(...shownIndices) : sectionIdx;

      const showAdd = sectionIdx === maxIdx;
      const showDelete = shownIndices.length > 1;

      const addQ = addItemsByTemplate.get(matchedTemplate);

      blocks.push(
        <div key={q.id} className="w-full min-w-0">
          <div className="flex items-center justify-between gap-4">
            <div className="text-base font-medium text-[#131315]">{q.question_text}</div>
            <div className="flex items-center justify-end gap-4">
              {showDelete ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={() => onDeleteItem(matchedTemplate, sectionIdx)}
                  className="text-black text-sm font-medium border border-white hover:border-black rounded-lg px-4 py-2"
                >
                  Delete
                </button>
              ) : null}
              {showAdd && addQ ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={() => onAddItem(addQ.item_template)}
                  className="flex items-center justify-end gap-2 px-4 py-2 rounded-lg border border-[#939393] text-sm text-[#131315] font-semibold bg-white hover:bg-[#F2FDFF] disabled:opacity-50"
                >
                  {addQ.question_text}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
      continue;
    }

    if (isReviewScreen) {
      flushGrid();
      const reviewErr = requiredFieldErrorMessage(q, answers, cleanedScreenTitle);
      blocks.push(
        <div key={q.id} className="w-full max-w-[668px]">
          <QuestionField
            q={q}
            answers={answers}
            setAnswers={setAnswers}
            cleanedScreenTitle={cleanedScreenTitle}
            sessionScreenId={sessionScreen.screen_id}
            actionBusy={actionBusy}
          />
          {showValidationErrors && reviewErr ? (
            <p className="text-sm text-red-600 mt-1" role="alert">
              {reviewErr}
            </p>
          ) : null}
          {q.help_text?.trim() ? <div className="mt-2" /> : null}
        </div>
      );
      continue;
    }

    // Question screens that embed tax/summary rows (e.g. AEAT payment): render full width like
    // review — not inside the 2-col grid with a duplicate label above.
    if (isNonInputQuestionFieldType(q.field_type)) {
      flushGrid();
      const displayErr = requiredFieldErrorMessage(q, answers, cleanedScreenTitle);
      blocks.push(
        <div key={q.id} className="w-full max-w-[668px]">
          <QuestionField
            q={q}
            answers={answers}
            setAnswers={setAnswers}
            cleanedScreenTitle={cleanedScreenTitle}
            sessionScreenId={sessionScreen.screen_id}
            actionBusy={actionBusy}
            inputsLocked={quoteInputsLocked}
          />
          {showValidationErrors && displayErr ? (
            <p className="text-sm text-red-600 mt-1" role="alert">
              {displayErr}
            </p>
          ) : null}
        </div>
      );
      continue;
    }

    const wideLabel = isQuestionLayoutWideLabel(q.layout);
    const gridErr = requiredFieldErrorMessage(q, answers, cleanedScreenTitle);
    gridCells.push(
      <div key={q.id} className={questionGridCellClass(q.layout)}>
        <div className="flex flex-col gap-3">
          <label className="block text-sm text-[#131315] font-medium">
            {cleanQuestionText(q.question_text, cleanedScreenTitle)}
            {q.required ? <span className="text-red-500 ml-1">*</span> : null}
          </label>

          <div className={wideLabel ? 'w-full lg:w-[310px] lg:max-w-[310px] shrink-0' : undefined}>
            <QuestionField
              q={q}
              answers={answers}
              setAnswers={setAnswers}
              cleanedScreenTitle={cleanedScreenTitle}
              sessionScreenId={sessionScreen.screen_id}
              actionBusy={actionBusy}
              inputsLocked={quoteInputsLocked}
            />
          </div>

          {showValidationErrors && gridErr ? (
            <p className="text-sm text-red-600 mt-0.5" role="alert">
              {gridErr}
            </p>
          ) : null}

          {fieldNote?.(q.id)}
        </div>
      </div>
    );
  }

  flushGrid();
  if (coownershipSkipBlock) blocks.push(coownershipSkipBlock);

  return <div className="w-full flex flex-col gap-y-8">{blocks}</div>;
}
